(() => {
  'use strict';
  const copy = key => window.PortraitCopy[key];
  const root = () => window.Shopify?.routes?.root || '/';
  const route = path => `${root()}${path}`;
  async function request(path, options = {}) {
    const timeout = AbortSignal.timeout(60000);
    const signal = options.signal ? AbortSignal.any([timeout, options.signal]) : timeout;
    const response = await fetch(route(path), { credentials: 'same-origin', ...options, signal });
    let data;
    try { data = await response.json(); } catch { throw new Error(copy('the_store_returned_an_unexpected_response_please_check_your_cart_before_trying_a')); }
    if (!response.ok) {
      const error = new Error(data.description || data.message || copy('the_store_could_not_complete_that_request'));
      error.definite = [400, 404, 422].includes(response.status);
      throw error;
    }
    return data;
  }
  function openDialog(dialog, opener) {
    if (!dialog || dialog.open) return;
    dialog._opener = opener || document.activeElement;
    dialog.showModal();
    document.documentElement.classList.add('dialog-open');
  }
  document.addEventListener('click', async event => {
    const open = event.target.closest('[data-dialog-open]');
    if (open) openDialog(document.getElementById(open.dataset.dialogOpen), open);
    const close = event.target.closest('[data-dialog-close]');
    if (close) close.closest('dialog')?.close();
    if (event.target instanceof HTMLDialogElement) {
      const rect = event.target.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) event.target.close();
    }
    const cart = event.target.closest('[data-cart-open]');
    if (cart) {
      event.preventDefault();
      try { await refreshCart(); openDialog(document.getElementById('CartDialog'), cart); }
      catch { location.assign(route('cart')); }
    }
  });
  document.addEventListener('close', event => {
    if (!(event.target instanceof HTMLDialogElement)) return;
    if (!document.querySelector('dialog[open]')) document.documentElement.classList.remove('dialog-open');
    event.target._opener?.focus?.();
  }, true);
  async function refreshCart() {
    const [cart, sections] = await Promise.all([request('cart.js'), request('?sections=cart-drawer,main-cart')]);
    const html = sections['cart-drawer'];
    if (!html) throw new Error(copy('your_cart_was_updated_open_the_full_cart_to_review_it'));
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const body = doc.getElementById('CartDrawerBody');
    if (!body) throw new Error(copy('unable_to_refresh_cart'));
    document.getElementById('CartDrawerBody')?.replaceChildren(...body.childNodes);
    const page = document.querySelector('.cart-page [data-cart-content]');
    if (page && sections['main-cart']) {
      const updated = new DOMParser().parseFromString(sections['main-cart'], 'text/html').querySelector('[data-cart-content]');
      if (updated) page.replaceWith(updated);
    }
    document.querySelectorAll('[data-cart-count]').forEach(node => { node.textContent = cart.item_count; });
    return cart;
  }
  let cartMutation = false;
  class CartContent extends HTMLElement {
    connectedCallback() {
      this.abort?.abort(); this.abort = new AbortController();
      this.addEventListener('change', event => {
        const input = event.target.closest('[data-quantity]');
        if (input) this.update(input.dataset.key, Number(input.value));
      }, { signal: this.abort.signal });
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-remove]');
        if (button) this.update(button.dataset.key, 0);
      }, { signal: this.abort.signal });
    }
    disconnectedCallback() { this.abort?.abort(); }
    async update(key, quantity) {
      if (cartMutation || !Number.isInteger(quantity) || quantity < 0) return;
      cartMutation = true;
      const error = this.querySelector('[data-cart-error]');
      const dialogId = this.closest('dialog')?.id;
      if (error) error.hidden = true;
      document.querySelectorAll('cart-content button,cart-content input').forEach(el => { el.disabled = true; });
      try {
        const cart = await request('cart.js');
        const selected = cart.items.find(item => item.key === key);
        if (!selected) throw new Error('This item is no longer in your cart.');
        const props = selected.properties || {};
        const family = props._royal_role === 'portrait' ? 'royal' : props._custom_role === 'product' ? 'custom' : null;
        const operation = family && props[`_${family}_operation`];
        // Services belong to the exact customization UUID, never another pet/product.
        const services = operation ? cart.items.filter(item => item.key !== key && item.properties?.[`_${family}_operation`] === operation && ['rush','digital','santa','sleeve','proof','gift-message'].includes(item.properties?.[`_${family}_role`])) : [];
        const updates = [...services, selected];
        try {
          for (const item of updates) {
            if (item.quantity === quantity) continue;
            const result = await request('cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: item.key, quantity }) });
            if (Array.isArray(result.items) && (result.items.find(line => line.key === item.key)?.quantity || 0) !== quantity) throw new Error('The requested quantity is not available.');
          }
        } catch (failure) {
          // A stock/network failure must never silently leave a partial group change.
          // Deleted lines cannot be recreated without their uploaded files; report and refresh.
          let restored = quantity !== 0;
          if (restored) {
            // Shopify can clamp a line to stock AND return 422. Even the failed
            // request may have changed it, so reconcile the entire original group.
            try {
              const current = await request('cart.js');
              for (const item of updates) {
                const actual = current.items.find(line => line.key === item.key);
                if (!actual) { restored = false; continue; }
                if (actual.quantity === item.quantity) continue;
                const result = await request('cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: item.key, quantity: item.quantity }) });
                if (Array.isArray(result.items) && result.items.find(line => line.key === item.key)?.quantity !== item.quantity) restored = false;
              }
            } catch { restored = false; }
          }
          await refreshCart();
          throw new Error(restored ? 'We could not apply that quantity. Your previous quantities have been restored.' : 'Some cart items may have changed. Please review your cart before checking out.');
        }
        await refreshCart();
        document.getElementById('SiteStatus').textContent = copy('cart_updated');
      } catch (e) {
        const visibleError = error?.isConnected ? error : (dialogId ? document.getElementById(dialogId)?.querySelector('[data-cart-error]') : document.querySelector('main cart-content [data-cart-error]')) || document.querySelector('cart-content [data-cart-error]');
        if (visibleError) { visibleError.textContent = `${e.message} Refresh the cart to confirm the current quantity.`; visibleError.hidden = false; }
      } finally {
        cartMutation = false;
        document.querySelectorAll('cart-content button,cart-content input').forEach(el => { el.disabled = false; });
      }
    }
  }
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.abort?.abort(); this.abort = new AbortController();
      this.input = this.querySelector('input[type=search]'); this.results = this.querySelector('[data-search-results]');
      this.input?.addEventListener('input', () => { clearTimeout(this.timer); this.timer = setTimeout(() => this.search(), 250); }, { signal: this.abort.signal });
    }
    disconnectedCallback() { this.abort?.abort(); this.fetchAbort?.abort(); clearTimeout(this.timer); }
    async search() {
      this.fetchAbort?.abort(); this.fetchAbort = new AbortController(); const controller = this.fetchAbort;
      const term = this.input.value.trim(); this.results.replaceChildren();
      if (!term) return;
      this.results.textContent = copy('searching');
      try {
        const data = await request(`search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=6`, { signal: this.fetchAbort.signal });
        if (this.fetchAbort !== controller || term !== this.input.value.trim()) return;
        const products = data.resources?.results?.products || []; this.results.replaceChildren();
        if (!products.length) { this.results.textContent = copy('no_products_found_try_another_search'); return; }
        const list = document.createElement('ul'); list.className = 'search-suggestions';
        products.forEach(product => {
          const li = document.createElement('li'), a = document.createElement('a');
          a.href = product.url; a.textContent = product.title;
          if (product.featured_image?.url) { const img = document.createElement('img'); img.src = product.featured_image.url; img.alt = ''; img.width = 64; img.height = 64; a.prepend(img); }
          li.append(a); list.append(li);
        }); this.results.append(list);
      } catch (e) { if (this.fetchAbort === controller && e.name !== 'AbortError') this.results.textContent = copy('suggestions_are_unavailable_press_search_to_view_results'); }
    }
  }
  class ContentCarousel extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.track = this.querySelector('[data-carousel-track]');
      if (!this.track) return;
      const update = () => { cancelAnimationFrame(this.frame); this.frame = requestAnimationFrame(() => this.update()); };
      this.track.addEventListener('scroll', update, { passive: true, signal: this.abort.signal });
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-carousel-direction]');
        if (button) this.move(Number(button.dataset.carouselDirection));
        const dot = event.target.closest('[data-carousel-page]');
        if (dot) this.track.scrollTo({ left: this.positions()[Number(dot.dataset.carouselPage)], behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      }, { signal: this.abort.signal });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home' || event.key === 'End') this.track.scrollTo({ left: event.key === 'Home' ? 0 : this.track.scrollWidth, behavior: 'auto' });
        else this.move(event.key === 'ArrowRight' ? 1 : -1);
      }, { signal: this.abort.signal });
      this.addEventListener('shopify:block:select', event => event.target.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' }), { signal: this.abort.signal });
      this.resize = new ResizeObserver(update); this.resize.observe(this.track); update();
    }
    disconnectedCallback() { this.abort?.abort(); this.abort = null; this.resize?.disconnect(); cancelAnimationFrame(this.frame); }
    step() {
      const child = this.track.firstElementChild;
      const width = child?.getBoundingClientRect().width || 0;
      return width ? width + (parseFloat(getComputedStyle(this.track).gap) || 0) : this.track.clientWidth * .85;
    }
    positions() {
      const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const step = this.step();
      if (!step || max < 2) return [0];
      const style = getComputedStyle(this.track);
      const inset = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
      const gap = parseFloat(style.gap) || 0;
      const visibleCount = Math.max(1, Math.floor((this.track.clientWidth - inset + gap + 1) / step));
      const stride = this.hasAttribute('data-carousel-paged') ? visibleCount : 1;
      const positions = [0];
      for (let offset = stride * step; offset < max - 2; offset += stride * step) positions.push(offset);
      positions.push(max);
      return positions;
    }
    move(direction) {
      const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      if (!this.hasAttribute('data-carousel-paged')) {
        const max = this.track.scrollWidth - this.track.clientWidth;
        if (this.hasAttribute('data-carousel-loop') && (direction < 0 && this.track.scrollLeft <= 3 || direction > 0 && this.track.scrollLeft >= max - 3)) this.track.scrollTo({ left: direction < 0 ? max : 0, behavior });
        else this.track.scrollBy({ left: direction * this.step(), behavior });
        return;
      }
      const positions = this.positions();
      const current = positions.reduce((best, target, index) => Math.abs(target - this.track.scrollLeft) < Math.abs(positions[best] - this.track.scrollLeft) ? index : best, 0);
      let next = current + direction;
      next = this.hasAttribute('data-carousel-loop') ? (next + positions.length) % positions.length : Math.max(0, Math.min(positions.length - 1, next));
      this.track.scrollTo({ left: positions[next], behavior });
    }
    update() {
      const children = [...this.track.children];
      const width = this.track.scrollWidth - this.track.clientWidth;
      // Read all geometry before writing controls, dots or live text.
      const positions = this.positions();
      const rect = this.track.getBoundingClientRect();
      const visible = children.map((child, i) => ({ rect: child.getBoundingClientRect(), i })).filter(item => item.rect.right > rect.left + 4 && item.rect.left < rect.right - 4);
      const controls = this.querySelector('[data-carousel-controls]');
      if (controls) controls.hidden = width < 2;
      const position = Math.max(0, this.track.scrollLeft);
      const previous = this.querySelector('[data-carousel-direction="-1"]');
      const next = this.querySelector('[data-carousel-direction="1"]');
      if (previous) previous.disabled = width < 2 || !this.hasAttribute('data-carousel-loop') && position <= 3;
      if (next) next.disabled = width < 2 || !this.hasAttribute('data-carousel-loop') && position >= width - 3;
      const dots = this.querySelector('[data-carousel-dots]');
      if (dots) {
        dots.hidden = positions.length < 2;
        if (dots.children.length !== positions.length) {
          dots.replaceChildren(...positions.map((_, index) => {
            const button = document.createElement('button'); button.type = 'button'; button.className = 'carousel-dot'; button.dataset.carouselPage = index;
            button.setAttribute('aria-label', `${copy('carousel_page')} ${index + 1}`); return button;
          }));
        }
        const nearest = positions.reduce((best, target, index) => Math.abs(target - position) < Math.abs(positions[best] - position) ? index : best, 0);
        [...dots.children].forEach((dot, index) => { const value=String(index===nearest); if(dot.getAttribute('aria-current')!==value)dot.setAttribute('aria-current',value); });
      }
      const meter = this.querySelector('[data-carousel-meter]');
      if (meter) meter.style.width = `${width > 0 ? 20 + 80 * Math.min(1, position / width) : 100}%`;

      const status = this.querySelector('[data-carousel-status]');
      if (status && visible.length) { const message=copy('carousel_position').replace('{first}',visible[0].i+1).replace('{last}',visible.at(-1).i+1).replace('{total}',children.length); if(status.textContent!==message)status.textContent=message; }
    }
  }
  class InspirationMarquee extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.button = this.querySelector('[data-marquee-toggle]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = this.motion.matches;
      this.button?.addEventListener('click', () => { this.userPaused = !this.userPaused; this.sync(); }, { signal: this.abort.signal });
      this.motion.addEventListener('change', () => { this.userPaused = this.motion.matches; this.sync(); }, { signal: this.abort.signal });
      this.sync();
    }
    sync() {
      this.toggleAttribute('data-paused', this.userPaused);
      if (this.button) {
        this.button.setAttribute('aria-pressed', String(this.userPaused));
        this.button.textContent = copy(this.userPaused ? 'play_gallery' : 'pause_gallery');
      }
    }
    disconnectedCallback() { this.abort?.abort(); this.abort = null; }
  }
  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.abort?.abort(); this.abort=new AbortController();const signal=this.abort.signal;
      const menus=[...this.querySelectorAll('.mega-nav')];
      const close=(except)=>menus.forEach(menu=>{if(menu!==except)menu.open=false;});
      menus.forEach(menu=>{
        menu.addEventListener('pointerenter',event=>{if(event.pointerType!=='touch'&&matchMedia('(min-width:1100px) and (hover:hover)').matches){clearTimeout(this.leaveTimer);close(menu);menu.open=true;}},{signal});
        menu.addEventListener('pointerleave',()=>{this.leaveTimer=setTimeout(()=>{if(!menu.contains(document.activeElement))menu.open=false;},180);},{signal});
        menu.addEventListener('toggle',()=>{if(menu.open)close(menu);},{signal});
      });
      this.addEventListener('keydown',event=>{if(event.key!=='Escape')return;const open=menus.find(m=>m.open);if(open){close();open.querySelector('summary').focus();event.preventDefault();}},{signal});
      this.addEventListener('click',event=>{if(!event.target.closest('.fp-mega-panel a,.fp-reviews-link'))return;close();this.querySelector('#MenuDialog[open]')?.close();},{signal});
      this.addEventListener('focusout',()=>{setTimeout(()=>{if(!this.contains(document.activeElement))close();},0);},{signal});
      document.addEventListener('click',event=>{if(!this.contains(event.target))close();},{signal});
    }
    disconnectedCallback(){this.abort?.abort();clearTimeout(this.leaveTimer);if(!document.querySelector('dialog[open]'))document.documentElement.classList.remove('dialog-open');}
  }
  class ResponsiveFooter extends HTMLElement {
    connectedCallback() {
      this.abort?.abort(); this.abort = new AbortController();
      const mobile = matchMedia('(max-width: 767px)');
      const update = () => {
        this.toggleAttribute('data-desktop', !mobile.matches);
        this.querySelectorAll('.footer-menu').forEach(menu => {
          menu.open = !mobile.matches;
          menu.querySelector('summary').tabIndex = mobile.matches ? 0 : -1;
        });
      };
      mobile.addEventListener('change', update, { signal: this.abort.signal });
      update();
    }
    disconnectedCallback() { this.abort?.abort(); }
  }
  if (!customElements.get('responsive-footer')) customElements.define('responsive-footer', ResponsiveFooter);
  if (!customElements.get('content-carousel')) customElements.define('content-carousel', ContentCarousel);
  if (!customElements.get('inspiration-marquee')) customElements.define('inspiration-marquee', InspirationMarquee);
  if (!customElements.get('site-header')) customElements.define('site-header', SiteHeader);
  if (!customElements.get('cart-content')) customElements.define('cart-content', CartContent);
  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
  window.PortraitStore = { request, route, refreshCart, openDialog };
})();
